import React, { useMemo } from 'react';
import styled from 'styled-components/macro';

const UNKNOWN_USER_INITIALS = "!!";

// Get initials to use for avatar image based on user's name
const getInitials = (name: string): string => {
    if (!name) {
        return UNKNOWN_USER_INITIALS;
    }

    const nameTrimmed = name.trim();

    if (!nameTrimmed) {
        return UNKNOWN_USER_INITIALS;
    }

    // strip out MSFT to avoid '[' from becoming second character of initials
    const parts = nameTrimmed
        .replace("[MSFT]", "")
        .split(" ")
        .filter(x => x);    // remove empty entries

    if (parts.length === 0) {
        // parts may be empty in case user name is just "[MSFT]" due to system bug or other unexpected case
        return UNKNOWN_USER_INITIALS;
    }

    // Take first letter of first 2 words or first 2 letters if it's only single word
    const initials =
        parts.length > 1
            ? `${parts[0][0]}${parts[parts.length - 1][0]}`
            : (parts[0].length > 1 ? parts[0].substring(0, 2) : parts[0]);

    return initials;
}

export interface AvatarProps {
    name: string;
    imageUrl?: string;
    imageTooltip?: string;

    className?: string;
    sizeInRems?: number;
    fontSizeInRems?: number;
}

export default function Avatar(props: AvatarProps): JSX.Element {
    const className = 'avatar-image ' + (props.className ?? "");

    const initials = useMemo(() => getInitials(props.name), [props.name]);

    return <Wrapper sizeInRems={props.sizeInRems} fontSizeInRems={props.fontSizeInRems}>
        {props.imageUrl && <img className={className} src={props.imageUrl} alt={props.imageTooltip ?? props.name ?? "avatar"} />}
        {!props.imageUrl && <span className={className}>{initials}</span>}
    </Wrapper>
}

const Wrapper = styled.span<{ sizeInRems?: number, fontSizeInRems?: number }>`
        > img, span {
            border-radius: 50%;

            display: inline-flex;
            justify-content: center;
            align-items: center;
            width: ${props => `${props.sizeInRems ?? 3}rem`};
            height: ${props => `${props.sizeInRems ?? 3}rem`};
            border-radius: 50%;
            background-color: var(--color-surface);
            border: solid 1px var(--color-on-surface-lightest);
        }

        > img {
            border-radius: 50%;
            object-fit: contain;
        }

        > span{
            background-color: #138378;
            color: white;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            font-size: ${props => `${props.fontSizeInRems ?? 1.4}rem`};
        }
`;
